import React, { useState, useMemo, useCallback } from 'react';
import { Box, Flex, Badge, ActionIcon, Modal } from '@mantine/core';
import { IconEye, IconDownload } from '@tabler/icons-react';
import theme from 'theme';
import P1SemiBold from 'theme/components/Typography/P1SemiBold';
import P2Regular from 'theme/components/Typography/P2Regular';
import { useNavigate } from 'react-router-dom';
import { DOCUMENT_DETAIL_PAGE_PATH } from 'routes/paths';
import useLang from 'lang';

interface DocumentItemProps {
  id: number;
  title: string;
  size: string;
  badges: Array<{ variant: string; text: string }>;
  path: string;
  type: string;
}

type FileType = 'image' | 'pdf' | 'video' | 'other';

// Constants moved outside component for better performance
const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'];
const VIDEO_EXTENSIONS = ['mp4', 'webm', 'ogg', 'mov', 'avi', 'wmv', 'flv', 'mkv'];

const MIME_TO_EXTENSION: Record<string, string> = {
  'application/pdf': 'pdf',
  'image/jpeg': 'jpg',
  'image/jpg': 'jpg',
  'image/png': 'png',
  'image/gif': 'gif',
  'image/bmp': 'bmp',
  'image/webp': 'webp',
  'image/svg+xml': 'svg',
  'video/mp4': 'mp4',
  'video/webm': 'webm',
  'video/ogg': 'ogg',
  'text/plain': 'txt',
  'application/msword': 'doc',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
  'application/vnd.ms-excel': 'xls',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
};

/**
 * Determines the file type based on the extension or type string
 */
const getFileType = (input?: string): FileType => {
  if (!input) return 'other';

  const mime = input.toLowerCase();

  if (mime === 'pdf' || mime.endsWith('/pdf')) {
    return 'pdf';
  }

  if (IMAGE_EXTENSIONS.includes(mime) || IMAGE_EXTENSIONS.some((ext) => mime.endsWith(`/${ext}`))) {
    return 'image';
  }

  if (VIDEO_EXTENSIONS.includes(mime) || VIDEO_EXTENSIONS.some((ext) => mime.endsWith(`/${ext}`))) {
    return 'video';
  }
  return 'other';
};

/**
 * Gets the correct file extension from MIME type or filename
 */
const getFileExtension = (mimeType: string, filename: string): string => {
  const fileExtMatch = filename.match(/\.([^.]+)$/);
  if (fileExtMatch) {
    return fileExtMatch[1];
  }

  return MIME_TO_EXTENSION[mimeType] || 'bin';
};

/**
 * DocumentItem component
 */
const DocumentItem: React.FC<DocumentItemProps> = ({ id, title, size, badges, path, type }) => {
  const { _t } = useLang();
  const [opened, setOpened] = useState(false);
  const navigate = useNavigate();
  const fileType = useMemo(() => getFileType(type), [type]);
  const extension = useMemo(() => getFileExtension(type, title), [type, title]);

  /**
   * Handles view action
   */
  const handleView = useCallback(() => {
    if (fileType === 'pdf') {
      navigate(DOCUMENT_DETAIL_PAGE_PATH.insert({ documentId: id }));
    } else {
      setOpened(true);
    }
  }, [fileType, navigate, id]);

  /**
   * Renders modal content based on file type
   */
  const renderModalContent = () => {
    switch (fileType) {
      case 'image':
        return <img src={path} alt={title} width="100%" />;
      case 'video':
        return (
          <Box>
            <video src={path} width="100%" height="auto" controls autoPlay={false} style={{ maxHeight: '500px' }} />
          </Box>
        );
      case 'other':
        return (
          <iframe src={path} width="100%" height="500px" style={{ border: 'none' }} title={_t('Document viewer')} />
        );
      default:
        return null;
    }
  };
  return (
    <>
      <Modal opened={opened} onClose={() => setOpened(false)} centered size="lg">
        {renderModalContent()}
      </Modal>

      <Flex
        direction="row"
        justify="space-between"
        align="center"
        bg={theme.white}
        p="1rem"
        style={{ borderRadius: '0.625rem' }}
        mb="0.5rem"
      >
        <Box>
          <Flex gap="0.5rem" maw="15.625rem" wrap="wrap">
            {badges.map((badge, index) => (
              <Badge key={index} variant={badge.variant}>
                {badge.text}
              </Badge>
            ))}
          </Flex>
          <P1SemiBold c={theme.colors?.gray?.[12]} mt="0.5rem">
            {title}
          </P1SemiBold>
          <P2Regular c={theme.colors?.gray?.[9]} mt="0.25rem">
            {size}
          </P2Regular>
        </Box>
        <Flex gap="0.5rem" align="center">
          <ActionIcon variant="white" color="#353E45" onClick={handleView}>
            <IconEye size="1.5rem" />
          </ActionIcon>
          <div style={{ width: '2px', height: '1.5rem', backgroundColor: '#F0F1F5' }}></div>
          <ActionIcon
            component="a"
            href={`${path}.${extension}?download=1`}
            download={title}
            variant="white"
            color="#353E45"
          >
            <IconDownload size="1.5rem" />
          </ActionIcon>
        </Flex>
      </Flex>
    </>
  );
};

export default DocumentItem;
